'use client';

import { Box, Typography, Container, Avatar, IconButton, TextField, Button, Grid, Paper, List, ListItem, ListItemIcon, ListItemText, Card, CardContent, Chip, FormControlLabel, Switch, Breadcrumbs, Stack, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import SvgIcon from '@mui/material/SvgIcon';
import dynamic from 'next/dynamic';
import { CameraAlt } from '@/lib/mui-optimized-imports';

function EditIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </SvgIcon>
  );
}

import React from 'react';
import { PlatformIcon } from '@/components/ConnectedAccountCard';
import { GoLiveObsCard } from '@/components/profile/GoLiveObsCard';
import { NativePublishDiagnosticsCard } from '@/components/profile/NativePublishDiagnosticsCard';
import { ScheduleHealthCard } from '@/components/profile/ScheduleHealthCard';
import { GitHubIdentityCard } from '@/components/profile/GitHubIdentityCard';
import { WorkspacePublishNotifyCard } from '@/components/profile/WorkspacePublishNotifyCard';
import { ZendeskConnectCard } from '@/components/profile/ZendeskConnectCard';
import { isZendeskIntegrationEnabled } from '@/lib/features/isZendeskIntegrationEnabled';
import { InboxFacebookPagesCard } from '@/components/profile/InboxFacebookPagesCard';
import { TelegramNotifyCard } from '@/components/profile/TelegramNotifyCard';
import { ReferralProgramSection } from '@/components/ReferralProgramSection';
import { DashboardBillingCard } from '@/components/billing/DashboardBillingCard';
const ThemeToggle = dynamic(() => import('@letsgo/theme-toggle').then(m => m.ThemeToggle || m), { ssr: false, loading: () => null });
// Phase 4.2: Heavy sections lazy-loaded with loading fallbacks
const IntegrationsPanel = dynamic(() => import('@/app/dashboard/profile/IntegrationsPanel').then(m => m.MemoizedIntegrationsPanel || m.default), { ssr: false, loading: () => <Typography>Loading integrations...</Typography> });

/** Returns a component that renders the Iconify platform icon for use in IntegrationsPanel. */
function makePlatformIcon(platformId: string) {
  return function IntegrationPlatformIcon(props: { fontSize?: string; style?: React.CSSProperties }) {
    return <PlatformIcon platformId={platformId} size={24} />;
  };
}
const ActivityLog = dynamic(() => import('@/app/dashboard/profile/ActivityLog').then(m => m.MemoizedActivityLog || m.default), { ssr: false, loading: () => <Typography>Loading activity...</Typography> });

interface Integration {
  id: string;
  name: string;
  icon: React.ElementType;
  connectedAs?: string;
  isConnected: boolean;
}

export type ProfilePageContentProps = { section?: string };

const PROFILE_SECTIONS = ['overview', 'account', 'security', 'integrations', 'api-keys', 'notifications', 'billing', 'more', 'settings'] as const;
type ProfileSectionId = typeof PROFILE_SECTIONS[number];
export type ActiveSection = ProfileSectionId | 'all-sections';

export function ProfilePageContent(props?: ProfilePageContentProps) {
  const { data: sessionData, status: sessionStatus, update } = useSession();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const section = props?.section;
  const isLoading = sessionStatus === 'loading';
  const [activeSection, setActiveSection] = React.useState<ActiveSection>('overview');
  const [displayName, setDisplayName] = React.useState(sessionData?.user?.name || '');
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null);

  // State for Personal Information fields
  const [firstName, setFirstName] = React.useState(sessionData?.user?.firstName || '');
  const [lastName, setLastName] = React.useState(sessionData?.user?.lastName || '');
  const [companyName, setCompanyName] = React.useState(sessionData?.user?.companyName || '');
  const [location, setLocation] = React.useState(sessionData?.user?.location || '');
  const [username, setUsername] = React.useState((sessionData?.user as { username?: string | null })?.username ?? '');

  // States to store original values for canceling edits
  const [originalFirstName, setOriginalFirstName] = React.useState(firstName);
  const [originalLastName, setOriginalLastName] = React.useState(lastName);
  const [originalCompanyName, setOriginalCompanyName] = React.useState(companyName);
  const [originalLocation, setOriginalLocation] = React.useState(location);
  const [originalUsername, setOriginalUsername] = React.useState(username);
  const [usernameError, setUsernameError] = React.useState('');

  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = React.useState(false);

  React.useEffect(() => {
    const u = (sessionData?.user as { username?: string | null })?.username ?? '';
    setUsername(u);
  }, [sessionData?.user]);

  React.useEffect(() => {
    if (isEditingName) return;
    const n = sessionData?.user?.name;
    if (n != null && n !== '') setDisplayName(n);
  }, [sessionData?.user?.name, isEditingName]);

  // State for Professional Details and Bio fields
  const [jobTitle, setJobTitle] = React.useState(sessionData?.user?.jobTitle || '');
  const [industry, setIndustry] = React.useState(sessionData?.user?.industry || '');
  const [bio, setBio] = React.useState(sessionData?.user?.bio || '');

  // States to store original values for canceling professional details edits
  const [originalJobTitle, setOriginalJobTitle] = React.useState(jobTitle);
  const [originalIndustry, setOriginalIndustry] = React.useState(industry);
  const [originalBio, setOriginalBio] = React.useState(bio);

  const [isEditingProfessionalInfo, setIsEditingProfessionalInfo] = React.useState(false);

  // User settings: accessibility (persisted in localStorage, aligned with WCAG 2.2)
  const REDUCED_MOTION_KEY = 'creatorflow-reduced-motion';
  const HIGH_CONTRAST_KEY = 'creatorflow-high-contrast';
  const EMPHASIZE_FOCUS_KEY = 'creatorflow-emphasize-focus';
  const TEXT_SIZE_KEY = 'creatorflow-text-size'; // 'normal' | 'large'

  const [reducedMotion, setReducedMotion] = React.useState(false);
  const [highContrast, setHighContrast] = React.useState(false);
  const [emphasizeFocus, setEmphasizeFocus] = React.useState(false);
  const [textSize, setTextSize] = React.useState<'normal' | 'large'>('normal');

  const applyAccessibilitySettings = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-reduced-motion', localStorage.getItem(REDUCED_MOTION_KEY) === 'true' ? 'true' : 'false');
    root.setAttribute('data-high-contrast', localStorage.getItem(HIGH_CONTRAST_KEY) === 'true' ? 'true' : 'false');
    root.setAttribute('data-emphasize-focus', localStorage.getItem(EMPHASIZE_FOCUS_KEY) === 'true' ? 'true' : 'false');
    root.setAttribute('data-text-size', localStorage.getItem(TEXT_SIZE_KEY) === 'large' ? 'large' : 'normal');
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    setReducedMotion(localStorage.getItem(REDUCED_MOTION_KEY) === 'true');
    setHighContrast(localStorage.getItem(HIGH_CONTRAST_KEY) === 'true');
    setEmphasizeFocus(localStorage.getItem(EMPHASIZE_FOCUS_KEY) === 'true');
    setTextSize((localStorage.getItem(TEXT_SIZE_KEY) === 'large' ? 'large' : 'normal') as 'normal' | 'large');
    applyAccessibilitySettings();
  }, [applyAccessibilitySettings]);

  const handleReducedMotionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setReducedMotion(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(REDUCED_MOTION_KEY, String(value));
      applyAccessibilitySettings();
    }
  };
  const handleHighContrastChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setHighContrast(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(HIGH_CONTRAST_KEY, String(value));
      applyAccessibilitySettings();
    }
  };
  const handleEmphasizeFocusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setEmphasizeFocus(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(EMPHASIZE_FOCUS_KEY, String(value));
      applyAccessibilitySettings();
    }
  };
  const handleTextSizeChange = (event: { target: { value: unknown } }) => {
    const value = (event.target.value === 'large' ? 'large' : 'normal') as 'normal' | 'large';
    setTextSize(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(TEXT_SIZE_KEY, value);
      applyAccessibilitySettings();
    }
  };

  // Focus management: move focus to main content when section changes (keyboard/screen reader)
  const mainContentRef = React.useRef<HTMLDivElement>(null);
  const prevSectionRef = React.useRef<ActiveSection>(activeSection);
  React.useEffect(() => {
    if (prevSectionRef.current !== activeSection) {
      prevSectionRef.current = activeSection;
      mainContentRef.current?.focus({ preventScroll: true });
    }
  }, [activeSection]);

  // Connected accounts / integrations: fetched from API (no mock data)
  const INTEGRATION_IDS: { id: string; name: string }[] = [
    { id: 'google', name: 'Google Account' },
    { id: 'google_drive', name: 'Google Drive' },
    { id: 'github', name: 'GitHub' },
    { id: 'tiktok', name: 'TikTok' },
    { id: 'youtube', name: 'YouTube' },
    { id: 'pinterest', name: 'Pinterest' },
    { id: 'facebook', name: 'Facebook' },
    { id: 'twitter', name: 'Twitter' },
    { id: 'whatsapp', name: 'Whatsapp' },
    { id: 'telegram', name: 'Telegram' },
    { id: 'reddit', name: 'Reddit' },
    { id: 'snapchat', name: 'Snapchat' },
    { id: 'vimeo', name: 'Vimeo' },
    { id: 'dribbble', name: 'Dribbble' },
    { id: 'dropbox', name: 'Dropbox' },
    { id: 'canva', name: 'Canva' },
    { id: 'slack', name: 'Slack' },
    ...(isZendeskIntegrationEnabled() ? [{ id: 'zendesk', name: 'Zendesk' } as const] : []),
  ];
  const [connectedAccountsMap, setConnectedAccountsMap] = React.useState<Record<string, { isConnected: boolean; username?: string }>>({});
  const searchParamsKey = searchParams?.toString() ?? '';
  const loadConnectedAccounts = React.useCallback(() => {
    if (!sessionData?.user?.id) return;
    fetch('/api/connected-accounts', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data: Record<string, { isConnected: boolean; username?: string }>) => setConnectedAccountsMap(data))
      .catch(() => {});
  }, [sessionData?.user?.id]);
  React.useEffect(() => {
    if (!sessionData?.user?.id) return;
    loadConnectedAccounts();
    const connected = searchParams?.get('connected')?.toLowerCase();
    if (connected) {
      setConnectedAccountsMap((prev) => ({
        ...prev,
        [connected]: { ...prev[connected], isConnected: true },
      }));
    }
    const t1 = window.setTimeout(loadConnectedAccounts, 400);
    const t2 = window.setTimeout(loadConnectedAccounts, 2000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [sessionData?.user?.id, searchParamsKey, loadConnectedAccounts]);
  const allIntegrations: Integration[] = INTEGRATION_IDS.map(({ id, name }) => {
    const acc = connectedAccountsMap[id];
    return {
      id,
      name,
      icon: makePlatformIcon(id),
      connectedAs: acc?.username,
      isConnected: acc?.isConnected ?? false,
    };
  });
  const connectedIntegrations = allIntegrations.filter(integration => integration.isConnected);
  const availableIntegrations = allIntegrations.filter(integration => !integration.isConnected);
  // Activity log: from API when available; empty until backend exists
  const activityItems: { primary: string; secondary: string }[] = [];

  React.useEffect(() => {
    if (!searchParams) return;
    const s = searchParams.get('section');
    if (s && PROFILE_SECTIONS.includes(s as ProfileSectionId)) {
      setActiveSection(s as ProfileSectionId);
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (!searchParams || searchParams.get('checkout') !== 'success') return;
    void update({});
    const params = new URLSearchParams(searchParams.toString());
    params.delete('checkout');
    const q = params.toString();
    const base = pathname ?? '/dashboard/profile';
    router.replace(q ? `${base}?${q}` : base, { scroll: false });
  }, [searchParams, update, router, pathname]);

  const handleNameSave = async () => {
    const trimmed = displayName.trim();
    setSaveError(null);
    try {
      const res = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed || null }),
      });
      let data: { name?: string | null; firstName?: string; lastName?: string; error?: string } = {};
      try {
        data = await res.json();
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        setSaveError(typeof data.error === 'string' ? data.error : 'Could not save display name.');
        return;
      }
      const saved = data.name ?? trimmed;
      setDisplayName(saved);
      if (data.firstName !== undefined) setFirstName(data.firstName);
      if (data.lastName !== undefined) setLastName(data.lastName);
      setIsEditingName(false);
      await update({});
    } catch {
      setSaveError('Could not save display name.');
    }
  };

  const handleNameCancel = () => {
    setDisplayName(sessionData?.user?.name || '');
    setIsEditingName(false);
  };

  const handlePersonalInfoSave = React.useCallback(async () => {
    setUsernameError('');
    setSaveError(null);
    const usernameToSave = username.trim().toLowerCase() || null;
    const usernameChanged = usernameToSave !== (originalUsername?.trim().toLowerCase() || null);
    try {
      const res = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(usernameChanged ? { username: usernameToSave } : {}),
          firstName,
          lastName,
          companyName,
          location,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setUsernameError(typeof data?.error === 'string' ? data.error : 'Failed to save profile.');
        return;
      }
      setOriginalFirstName(data.firstName ?? firstName);
      setOriginalLastName(data.lastName ?? lastName);
      setOriginalCompanyName(data.companyName ?? companyName);
      setOriginalLocation(data.location ?? location);
      const u = data.username ?? usernameToSave ?? '';
      setOriginalUsername(u);
      setUsername(u);
      setFirstName(data.firstName ?? firstName);
      setLastName(data.lastName ?? lastName);
      setCompanyName(data.companyName ?? companyName);
      setLocation(data.location ?? location);
      if (data.name != null) setDisplayName(data.name);
      setIsEditingPersonalInfo(false);
      await update({});
    } catch {
      setUsernameError('Failed to save profile.');
    }
  }, [firstName, lastName, companyName, location, username, originalUsername, update]);

  const handlePersonalInfoCancel = () => {
    setFirstName(originalFirstName);
    setLastName(originalLastName);
    setCompanyName(originalCompanyName);
    setLocation(originalLocation);
    setUsername(originalUsername);
    setUsernameError('');
    setIsEditingPersonalInfo(false);
  };

  const handleEditPersonalInfo = () => {
    setOriginalFirstName(firstName);
    setOriginalLastName(lastName);
    setOriginalCompanyName(companyName);
    setOriginalLocation(location);
    setOriginalUsername(username);
    setIsEditingPersonalInfo(true);
  };

  const handleProfessionalInfoSave = async () => {
    setSaveError(null);
    try {
      const res = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobTitle, industry, bio }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveError(typeof data?.error === 'string' ? data.error : 'Could not save professional details.');
        return;
      }
      const jt = data.jobTitle ?? jobTitle;
      const ind = data.industry ?? industry;
      const b = data.bio ?? bio;
      setOriginalJobTitle(jt);
      setOriginalIndustry(ind);
      setOriginalBio(b);
      setJobTitle(jt);
      setIndustry(ind);
      setBio(b);
      setIsEditingProfessionalInfo(false);
      await update({});
    } catch {
      setSaveError('Could not save professional details.');
    }
  };

  const handleProfessionalInfoCancel = () => {
    setJobTitle(originalJobTitle);
    setIndustry(originalIndustry);
    setBio(originalBio);
    setIsEditingProfessionalInfo(false);
  };

  const handleEditProfessionalInfo = () => {
    setOriginalJobTitle(jobTitle);
    setOriginalIndustry(industry);
    setOriginalBio(bio);
    setIsEditingProfessionalInfo(true);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      // In a real application, you would upload this file to a server
    }
  };

  const navItems: { id: ProfileSectionId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'account', label: 'Account' },
    { id: 'security', label: 'Security' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'api-keys', label: 'API keys' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'billing', label: 'Billing' },
    { id: 'more', label: 'More' },
    { id: 'settings', label: 'Settings' },
  ];

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, maxWidth: '100%', px: { xs: 2, sm: 3 } }} component="div">
        <Box sx={{ py: 6, textAlign: 'center' }} role="status" aria-live="polite" aria-label="Loading profile">
          <Typography color="text.secondary">Loading profile…</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, maxWidth: '100%', px: { xs: 2, sm: 3 } }} component="div">
      {/* Skip link: visible on focus for keyboard users (WCAG 2.4.1 Bypass Blocks) */}
      <a
        href="#profile-main"
        onClick={(e) => {
          e.preventDefault();
          mainContentRef.current?.focus();
        }}
        style={{ position: 'absolute', left: -9999, zIndex: 9999 }}
        className="skip-link"
      >
        Skip to main content
      </a>

      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link href="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography component="span" variant="body2" color="text.secondary" sx={{ '&:hover': { textDecoration: 'underline' } }}>Dashboard</Typography>
        </Link>
        <Typography variant="body2" color="text.primary">Profile</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, minWidth: 0 }}>
        {/* Left sidebar - profile section nav (ARIA, 44px touch targets) */}
        <Box
          component="nav"
          aria-label="Profile sections"
          sx={{ width: { md: 260 }, flexShrink: 0, minWidth: 0 }}
        >
          <Typography id="profile-nav-heading" variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }} component="h2">Overview</Typography>
          <Grid container spacing={1} sx={{ maxWidth: 260 }}>
            {navItems.map(({ id, label }) => (
              <Grid item xs={6} sm={4} key={id}>
                <Button
                  fullWidth
                  variant={activeSection === id ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                  onClick={() => setActiveSection(id)}
                  aria-current={activeSection === id ? 'true' : undefined}
                  aria-label={`Show ${label} section`}
                  sx={{ minHeight: 44, minWidth: 44, textTransform: 'none', justifyContent: 'center' }}
                >
                  {label}
                </Button>
              </Grid>
            ))}
            <Grid item xs={6} sm={4}>
              <Button
                fullWidth
                variant={activeSection === 'all-sections' ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                onClick={() => setActiveSection('all-sections')}
                aria-current={activeSection === 'all-sections' ? 'true' : undefined}
                aria-label="Show all sections"
                sx={{ minHeight: 44, minWidth: 44, textTransform: 'none', justifyContent: 'center' }}
              >
                All
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Main content - single panel per section (focus target, live region) */}
        <Box
          ref={mainContentRef}
          component="main"
          id="profile-main"
          tabIndex={-1}
          role="main"
          aria-label={`Profile: ${activeSection === 'overview' ? 'Overview' : activeSection === 'all-sections' ? 'All sections' : navItems.find((n) => n.id === activeSection)?.label ?? activeSection}`}
          aria-live="polite"
          aria-atomic="true"
          sx={{ flex: 1, minWidth: 0, outline: 'none', maxWidth: { md: 720 }, width: '100%' }}
        >
          {activeSection === 'overview' && (
          <Box id="overview" sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>Overview</Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'auto auto 1fr auto' },
                alignItems: 'center',
                gap: 2,
                mb: 3,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              <Avatar
                alt={sessionData?.user?.name || 'User'}
                sx={{ width: 72, height: 72, justifySelf: { xs: 'start', sm: 'auto' }, position: 'relative', overflow: 'hidden' }}
              >
                <Image
                  src={avatarPreview || sessionData?.user?.image || `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72"><circle fill="%23e0e0e0" cx="36" cy="36" r="36"/><circle fill="%239e9e9e" cx="36" cy="30" r="10"/><path fill="%239e9e9e" d="M36 52c-9 0-16-5-16-11s7-11 16-11 16 5 16 11-7 11-16 11z"/></svg>')}`}
                  alt={sessionData?.user?.name || 'User'}
                  width={72}
                  height={72}
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                  priority
                  unoptimized
                />
              </Avatar>
              <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleAvatarChange} />
              <Button type="button" variant="outlined" color="primary" size="small" startIcon={<CameraAlt fontSize="small" />} onClick={handleAvatarClick} sx={{ justifySelf: { xs: 'start', sm: 'auto' }, minHeight: 44 }} aria-label="Change profile photo">
                CHANGE PHOTO
              </Button>
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                {isEditingName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <TextField variant="outlined" size="small" value={displayName} onChange={(e) => setDisplayName(e.target.value)} sx={{ minWidth: 120, maxWidth: '100%' }} />
                    <Button type="button" size="small" onClick={handleNameSave}>Save</Button>
                    <Button type="button" size="small" onClick={handleNameCancel}>Cancel</Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>

                    <Typography variant="h6" noWrap sx={{ maxWidth: '100%' }}>{displayName}</Typography>
                    <IconButton size="small" onClick={() => setIsEditingName(true)} aria-label="Edit display name"><EditIcon fontSize="small" /></IconButton>
                    <Typography component="span" variant="body2" color="primary" sx={{ cursor: 'pointer', flexShrink: 0, '&:hover': { textDecoration: 'underline' } }} onClick={() => setIsEditingName(true)}>EDIT NAME</Typography>
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>{sessionData?.user?.email}</Typography>
              </Box>
              <Box sx={{ justifySelf: { xs: 'start', sm: 'end' } }}>
                <ThemeToggle />
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick Actions</Typography>
            <Stack direction="column" spacing={1} sx={{ mb: 3 }} role="group" aria-label="Quick actions">
              <Button type="button" variant="outlined" color="primary" size="small" onClick={() => setActiveSection('account')} sx={{ minHeight: 44, justifyContent: 'flex-start' }}>EDIT PROFILE</Button>
              {(sessionData?.user?.role === 'ADMIN' || sessionData?.user?.role === 'OWNER') && (
                <Button type="button" variant="outlined" color="primary" size="small" component={Link} href="/dashboard/admin" sx={{ minHeight: 44, justifyContent: 'flex-start' }}>ADMIN PANEL</Button>
              )}
              {sessionData?.user?.role === 'OWNER' && (
                <Button type="button" variant="outlined" color="primary" size="small" component={Link} href="/dashboard/admin/owner-settings" sx={{ minHeight: 44, justifyContent: 'flex-start' }}>OWNER SETTINGS</Button>
              )}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Complete your profile, manage integrations, view activity, and more.</Typography>
            <Typography component="span" variant="body2" color="primary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => setActiveSection('all-sections')}>View all sections in one page</Typography>
          </Box>
          )}

          {activeSection === 'account' && (
          <Grid container spacing={3}>
            <Grid item xs={12} id="account">
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Personal Information</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={!isEditingPersonalInfo}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={!isEditingPersonalInfo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name (Optional)"
                    variant="outlined"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={!isEditingPersonalInfo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    variant="outlined"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    disabled={!isEditingPersonalInfo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Username"
                    variant="outlined"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setUsernameError(''); }}
                    disabled={!isEditingPersonalInfo}
                    error={!!usernameError}
                    helperText={usernameError || 'Used in your referral link (e.g. /refer/your-username). Lowercase letters, numbers, and hyphens only.'}
                    placeholder="e.g. jane-doe"
                  />
                </Grid>
              </Grid>
              <Box>
                {isEditingPersonalInfo ? (
                  <>
                    <Button type="button" variant="contained" color="primary" onClick={() => void handlePersonalInfoSave()} sx={{ mr: 1 }}>Save</Button>
                    <Button type="button" variant="outlined" onClick={handlePersonalInfoCancel}>Cancel</Button>
                  </>
                ) : (
                  <Button type="button" variant="outlined" onClick={handleEditPersonalInfo}>Edit Personal Info</Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Professional Details & Bio</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    variant="outlined"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    disabled={!isEditingProfessionalInfo}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Industry"
                    variant="outlined"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    disabled={!isEditingProfessionalInfo}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bio"
                    variant="outlined"
                    multiline
                    rows={4}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    disabled={!isEditingProfessionalInfo}
                  />
                </Grid>
              </Grid>
              <Box>
                {isEditingProfessionalInfo ? (
                  <>
                    <Button type="button" variant="contained" color="primary" onClick={handleProfessionalInfoSave} sx={{ mr: 1 }}>Save</Button>
                    <Button type="button" variant="outlined" onClick={handleProfessionalInfoCancel}>Cancel</Button>
                  </>
                ) : (
                  <Button type="button" variant="outlined" onClick={handleEditProfessionalInfo}>Edit Professional Info</Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
          </Grid>
          )}

          {activeSection === 'security' && (
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Security & Login Settings</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
              <Box>
                <Button type="button" variant="contained" color="primary" sx={{ mr: 1 }}>Change Password</Button>
                <Button type="button" variant="outlined">Cancel</Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Sign-in activity will appear here when available.
              </Typography>
            </Box>
          </Paper>
          )}

          {activeSection === 'integrations' && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Connected Accounts / Integrations</Typography>
              <List>
                {connectedIntegrations.map((integration) => (
                  <ListItem key={integration.id} secondaryAction={
                    <Button type="button" variant="contained" size="small">Disconnect</Button>
                  }>
                <ListItemIcon sx={{ minWidth: '30px' }}>
                      <PlatformIcon platformId={integration.id} size={24} />
                    </ListItemIcon>
                    <ListItemText primary={integration.name} secondary={integration.connectedAs ? `Connected as ${integration.connectedAs}` : 'Connected'} />
                  </ListItem>
                ))}
              </List>
            </Box>

      <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>Discover Integrations</Typography>
              <IntegrationsPanel connectedIntegrations={connectedIntegrations} availableIntegrations={availableIntegrations} />
              <WorkspacePublishNotifyCard />
              {isZendeskIntegrationEnabled() ? <ZendeskConnectCard /> : null}
              <InboxFacebookPagesCard />
              <ScheduleHealthCard />
              <NativePublishDiagnosticsCard />
              <TelegramNotifyCard />
              <GoLiveObsCard />
              <GitHubIdentityCard />
            </Box>
          </Paper>
          )}

          {activeSection === 'api-keys' && (
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>API keys</Typography>
                <Typography variant="body2" color="text.secondary">Coming soon. Manage API keys for integrations and automation.</Typography>
              </Box>
            </Paper>
          )}

          {activeSection === 'notifications' && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Notifications & Preferences</Typography>
              <List>
                <ListItem>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Email Notifications"
                  />
                </ListItem>
                <ListItem>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="In-App Notifications"
                  />
                </ListItem>
                <ListItem>
                  <FormControlLabel
                    control={<Switch />}
                    label="Marketing & Promotional Emails"
                  />
                </ListItem>
              </List>
            </Box>
          </Paper>
          )}

          {activeSection === 'billing' && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" gutterBottom>Subscription & Billing</Typography>
              <DashboardBillingCard />
            </Box>
          </Paper>
          )}

          {activeSection === 'settings' && (
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom>User settings</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Control how the app looks and behaves. These options help make the experience more accessible.
                </Typography>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mt: 3 }}>Accessibility</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>These options align with WCAG 2.2 recommendations. They help with low vision, motion sensitivity, and keyboard navigation.</Typography>
                <List dense disablePadding>
                  <ListItem sx={{ px: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ flex: '1 1 200px' }}>Theme</Typography>
                      <ThemeToggle />
                    </Box>
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <FormControlLabel
                      control={<Switch checked={reducedMotion} onChange={handleReducedMotionChange} color="primary" />}
                      label={
                        <Box>
                          <Typography variant="body2">Reduce motion</Typography>
                          <Typography variant="caption" color="text.secondary">Minimize animations and transitions (WCAG 2.2.2).</Typography>
                        </Box>
                      }
                      labelPlacement="end"
                      sx={{ alignItems: 'flex-start', mx: 0 }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <FormControlLabel
                      control={<Switch checked={highContrast} onChange={handleHighContrastChange} color="primary" />}
                      label={
                        <Box>
                          <Typography variant="body2">High contrast</Typography>
                          <Typography variant="caption" color="text.secondary">Stronger borders and emphasis (WCAG 1.4.3 Contrast).</Typography>
                        </Box>
                      }
                      labelPlacement="end"
                      sx={{ alignItems: 'flex-start', mx: 0 }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <FormControlLabel
                      control={<Switch checked={emphasizeFocus} onChange={handleEmphasizeFocusChange} color="primary" />}
                      label={
                        <Box>
                          <Typography variant="body2">Emphasize focus</Typography>
                          <Typography variant="caption" color="text.secondary">Make keyboard focus ring more visible (WCAG 2.4.11, 2.4.12).</Typography>
                        </Box>
                      }
                      labelPlacement="end"
                      sx={{ alignItems: 'flex-start', mx: 0 }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, display: 'block' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="text-size-label">Text size</InputLabel>
                        <Select labelId="text-size-label" value={textSize} label="Text size" onChange={handleTextSizeChange}>
                          <MenuItem value="normal">Normal (100%)</MenuItem>
                          <MenuItem value="large">Large (125%)</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">Larger text helps readability (WCAG 1.4.4 Resize text).</Typography>
                    </Box>
                  </ListItem>
                </List>
              </Box>
            </Paper>
          )}

          {activeSection === 'more' && (
            <>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" component="h2" gutterBottom>Activity Log / History</Typography>
                  <ActivityLog activities={activityItems} onViewAll={() => { console.log('view all clicked'); }} />
                </Box>
              </Paper>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <ReferralProgramSection />
              </Paper>
            </>
          )}

          {activeSection === 'all-sections' && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom>Personal Information</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="First Name" variant="outlined" value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={!isEditingPersonalInfo} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name" variant="outlined" value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={!isEditingPersonalInfo} /></Grid>
                      <Grid item xs={12}><TextField fullWidth label="Company Name (Optional)" variant="outlined" value={companyName} onChange={(e) => setCompanyName(e.target.value)} disabled={!isEditingPersonalInfo} /></Grid>
                      <Grid item xs={12}><TextField fullWidth label="Location" variant="outlined" value={location} onChange={(e) => setLocation(e.target.value)} disabled={!isEditingPersonalInfo} /></Grid>
                      <Grid item xs={12}><TextField fullWidth label="Username" variant="outlined" value={username} onChange={(e) => { setUsername(e.target.value); setUsernameError(''); }} disabled={!isEditingPersonalInfo} error={!!usernameError} helperText={usernameError || 'Used in referral link (/refer/your-username)'} placeholder="e.g. jane-doe" /></Grid>
                    </Grid>
                    <Box>
                      {isEditingPersonalInfo ? (<><Button type="button" variant="contained" color="primary" onClick={handlePersonalInfoSave} sx={{ mr: 1 }}>Save</Button><Button type="button" variant="outlined" onClick={handlePersonalInfoCancel}>Cancel</Button></>) : (<Button type="button" variant="outlined" onClick={handleEditPersonalInfo}>Edit Personal Info</Button>)}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom>Professional Details & Bio</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Job Title" variant="outlined" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} disabled={!isEditingProfessionalInfo} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Industry" variant="outlined" value={industry} onChange={(e) => setIndustry(e.target.value)} disabled={!isEditingProfessionalInfo} /></Grid>
                      <Grid item xs={12}><TextField fullWidth label="Bio" variant="outlined" multiline rows={4} value={bio} onChange={(e) => setBio(e.target.value)} disabled={!isEditingProfessionalInfo} /></Grid>
                    </Grid>
                    <Box>
                      {isEditingProfessionalInfo ? (<><Button type="button" variant="contained" color="primary" onClick={handleProfessionalInfoSave} sx={{ mr: 1 }}>Save</Button><Button type="button" variant="outlined" onClick={handleProfessionalInfoCancel}>Cancel</Button></>) : (<Button type="button" variant="outlined" onClick={handleEditProfessionalInfo}>Edit Professional Info</Button>)}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ mb: 4 }}><Typography variant="h5" component="h2" gutterBottom>Security & Login Settings</Typography><Grid container spacing={2} sx={{ mb: 2 }}><Grid item xs={12}><TextField fullWidth label="Current Password" type="password" variant="outlined" sx={{ mb: 2 }} /></Grid><Grid item xs={12}><TextField fullWidth label="New Password" type="password" variant="outlined" sx={{ mb: 2 }} /></Grid><Grid item xs={12}><TextField fullWidth label="Confirm New Password" type="password" variant="outlined" sx={{ mb: 2 }} /></Grid></Grid><Box><Button type="button" variant="contained" color="primary" sx={{ mr: 1 }}>Change Password</Button><Button type="button" variant="outlined">Cancel</Button></Box><Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>Sign-in activity will appear here when available.</Typography></Box></Paper></Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ mb: 4 }}><Typography variant="h5" component="h2" gutterBottom>Connected Accounts / Integrations</Typography><List>{connectedIntegrations.map((integration) => (<ListItem key={integration.id} secondaryAction={<Button type="button" variant="contained" size="small">Disconnect</Button>}><ListItemIcon sx={{ minWidth: '30px' }}><PlatformIcon platformId={integration.id} size={24} /></ListItemIcon><ListItemText primary={integration.name} secondary={integration.connectedAs ? `Connected as ${integration.connectedAs}` : 'Connected'} /></ListItem>))}</List></Box><Box><Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>Discover Integrations</Typography><IntegrationsPanel connectedIntegrations={connectedIntegrations} availableIntegrations={availableIntegrations} /></Box></Paper></Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="h6" gutterBottom>API keys</Typography><Typography variant="body2" color="text.secondary">Coming soon.</Typography></Box></Paper></Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ mb: 4 }}><Typography variant="h5" component="h2" gutterBottom>Notifications & Preferences</Typography><List><ListItem><FormControlLabel control={<Switch defaultChecked />} label="Email Notifications" /></ListItem><ListItem><FormControlLabel control={<Switch defaultChecked />} label="In-App Notifications" /></ListItem><ListItem><FormControlLabel control={<Switch />} label="Marketing & Promotional Emails" /></ListItem></List></Box></Paper></Grid>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom>Subscription & Billing</Typography>
                    <DashboardBillingCard />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ mb: 4 }}><Typography variant="h5" component="h2" gutterBottom>Activity Log / History</Typography><ActivityLog activities={activityItems} onViewAll={() => {}} /></Box></Paper></Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><ReferralProgramSection /></Paper></Grid>
              <Grid item xs={12}><Paper elevation={1} sx={{ p: 3, mb: 3 }}><Box sx={{ mb: 4 }}><Typography variant="h5" component="h2" gutterBottom>User settings</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Accessibility: Theme, reduce motion, high contrast, emphasize focus, text size.</Typography><Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}><ThemeToggle /><FormControlLabel control={<Switch checked={reducedMotion} onChange={handleReducedMotionChange} />} label="Reduce motion" /><FormControlLabel control={<Switch checked={highContrast} onChange={handleHighContrastChange} />} label="High contrast" /><FormControlLabel control={<Switch checked={emphasizeFocus} onChange={handleEmphasizeFocusChange} />} label="Emphasize focus" /><FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Text size</InputLabel><Select value={textSize} label="Text size" onChange={handleTextSizeChange}><MenuItem value="normal">Normal</MenuItem><MenuItem value="large">Large</MenuItem></Select></FormControl></Box></Box></Paper></Grid>
            </Grid>
          )}

          {/* Sign out - bottom of page in all views (44px min touch target) */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }} role="region" aria-label="Account actions">
            <Button type="button" variant="outlined" color="secondary" onClick={() => signOut({ callbackUrl: '/' })} fullWidth sx={{ py: 1.5, minHeight: 44 }} aria-label="Sign out of your account">
              Sign out
            </Button>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ProfilePageContent;
